import { getHeaders } from "../../../utils/contextHelpers";

const pendingRequests = new Map();

export const deduplicate = (key, fetcher) => {
    if (pendingRequests.has(key)) return pendingRequests.get(key);

    const promise = fetcher().finally(() => {
        setTimeout(() => pendingRequests.delete(key), 500);
    });

    pendingRequests.set(key, promise);
    return promise;
};

export const request = async (url, options = {}) => {
    const { method = "GET", headers, body, credentials = "include", redirect } = options;
    const response = await fetch(url, { method, headers, body, credentials, redirect });

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
        ? await response.json()
        : await response.text();

    if (!response.ok) {
        let message = response.statusText || "Error de la API";
        if (typeof payload === "string") {
            const jsonMatch = payload.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                try {
                    const parsed = JSON.parse(jsonMatch[0]);
                    message = parsed?.error_message || parsed?.message || parsed?.ERROR || message;
                } catch {
                    message = payload || message;
                }
            } else {
                message = payload || message;
            }
        } else if (payload && typeof payload === "object") {
            message = payload?.error_message || payload?.message || payload?.ERROR || message;
        }
        const error = new Error(message);
        error.status = response.status;
        error.body = payload;
        error.conflict = response.status === 409;
        throw error;
    }

    return payload;
};

export const buildQuery = (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null) qs.append(key, String(item));
            });
        } else {
            qs.append(key, String(value));
        }
    });
    return qs.toString();
};

export const api = (name, urlFn, options = {}) => {
    return (...args) => {
        const key = `${name}-${JSON.stringify(args)}`;
        return deduplicate(key, () => {
            const { method = "GET", headers: extraHeaders, credentials, redirect } = options;
            const headers = getHeaders();
            if (extraHeaders) {
                Object.entries(extraHeaders).forEach(([k, v]) => headers.append(k, v));
            }
            return request(urlFn(...args), { method, headers, credentials, redirect });
        });
    };
};

export const apiPost = (name, urlFn, options = {}) => {
    return (...args) => {
        const key = `${name}-${JSON.stringify(args)}`;
        return deduplicate(key, async () => {
            const { body, contentType = "application/x-www-form-urlencoded", credentials, redirect } = options;
            const headers = getHeaders();
            headers.delete("Content-Type");
            headers.append("Content-Type", contentType);
            const resolvedBody = typeof body === "function" ? await body(...args) : body;
            return request(urlFn(...args), { method: "POST", headers, body: resolvedBody, credentials, redirect });
        });
    };
};

export const apiPut = (name, urlFn, options = {}) => {
    return (...args) => {
        const key = `${name}-${JSON.stringify(args)}`;
        return deduplicate(key, async () => {
            const { body, contentType = "application/x-www-form-urlencoded", credentials, redirect } = options;
            const headers = getHeaders();
            headers.delete("Content-Type");
            headers.append("Content-Type", contentType);
            const resolvedBody = typeof body === "function" ? await body(...args) : body;
            return request(urlFn(...args), { method: "PUT", headers, body: resolvedBody, credentials, redirect });
        });
    };
};